import { useCallback, useRef, useState } from "react";

// HTML5 native drag-and-drop 으로 활동지 목록의 순서/세트를 바꾸는 훅.
// 블록 드래그(useTaskEditorBlockDrag)와 달리 세트 간 이동·미분류 해제까지 다룬다.
// PC 전용 (터치 미고려).
const LIST_DRAG_MIME = "application/x-task-editor-list-task";

// 드롭 위치: 어떤 세트(미분류=9999)의 어떤 활동지 "앞"인지. null = 섹션 끝.
export interface ListDropTarget {
  setNumber: number;
  beforeTaskId: string | null;
}

export interface TaskListDragState {
  draggingTaskId: string | null;
  dropTarget: ListDropTarget | null;
}

function isSameTarget(prev: ListDropTarget | null, next: ListDropTarget) {
  return (
    prev !== null &&
    prev.setNumber === next.setNumber &&
    prev.beforeTaskId === next.beforeTaskId
  );
}

export function useTaskEditorListDrag(
  onReorder: (
    draggedTaskId: string,
    targetSetNumber: number,
    beforeTaskId: string | null
  ) => void
) {
  const [dragState, setDragState] = useState<TaskListDragState>({
    draggingTaskId: null,
    dropTarget: null,
  });
  const draggingTaskIdRef = useRef<string | null>(null);

  const handleDragStart = useCallback(
    (event: React.DragEvent, taskId: string) => {
      draggingTaskIdRef.current = taskId;
      setDragState({ draggingTaskId: taskId, dropTarget: null });
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData(LIST_DRAG_MIME, taskId);
      // 행 전체가 draggable 이라 브라우저 기본 드래그 미리보기(행 스냅샷)를 그대로 사용.
    },
    []
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent, target: ListDropTarget) => {
      if (!draggingTaskIdRef.current) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setDragState((prev) =>
        isSameTarget(prev.dropTarget, target)
          ? prev
          : { ...prev, dropTarget: target }
      );
    },
    []
  );

  const reset = useCallback(() => {
    draggingTaskIdRef.current = null;
    setDragState({ draggingTaskId: null, dropTarget: null });
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent, target: ListDropTarget) => {
      event.preventDefault();
      event.stopPropagation();
      const draggedTaskId =
        event.dataTransfer.getData(LIST_DRAG_MIME) ||
        draggingTaskIdRef.current ||
        "";
      reset();
      if (!draggedTaskId) return;
      onReorder(draggedTaskId, target.setNumber, target.beforeTaskId);
    },
    [onReorder, reset]
  );

  return {
    dragState,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd: reset,
  };
}
